import { UserProfession } from '~/enums/UserProfession';
import {
  CredentialInputTypeEnum,
  CredentialTypeEnum,
  ICredential,
  ICredentialV2FileType,
  ILicense,
  ILicenseDetailV2,
  ILicenseV2,
  IS3File,
} from '~/models';

/**
 * converting response license v2 (from credentials service) to v1 (monolith) response
 *
 * @param data
 * @returns
 */
export function convertLicensesV2toV1(data: ILicenseV2[]): ILicense[] {
  return data
    .map((entry) => ({
      status: entry.status,
      license: '',
      state: entry.state,
      profession: entry.profession,
      school: '',
      subProfession: entry.subProfession,
      addedAt: new Date(entry?.createdAt),
      credentials: [
        {
          entry_field: {
            license: '',
            issue_date: entry.createdAt
              ? new Date(entry.createdAt)
              : undefined,
            expiration_date: entry.expiredAt
              ? new Date(entry.expiredAt)
              : undefined,
            normalizedLicense: '',
          },
          credential_type: CredentialTypeEnum.StateLicense,
          credential_input_type: CredentialInputTypeEnum.EntryField,
          files: [],
          addedAt: entry.createdAt,
        },
      ],
      id: entry.id,
      professionName: UserProfession.getName(entry.profession)!,
    }))
    .sort((a, b) => b.addedAt.getTime() - a.addedAt.getTime());
}

/**
 * converting response license detail v2 (from credentials service) to v1 (monolith) response
 *
 * @param entry
 * @returns
 */
export function convertLicenseDetailV2toV1(entry: ILicenseDetailV2): ILicense {
  const radiologyFiles: IS3File[] = [];
  const daCertificateFiles: IS3File[] = [];
  const efdaCertificateFiles: IS3File[] = [];
  const cprBlsFiles: IS3File[] = [];

  entry?.files?.forEach((item) => {
    const file: IS3File = {
      file_id: item.id,
      s3_url: item.url,
    };
    switch (item.type) {
      case ICredentialV2FileType.radiology:
        radiologyFiles.push(file);
        break;
      case ICredentialV2FileType.daCertificate:
        daCertificateFiles.push(file);
        break;
      case ICredentialV2FileType.efdaCertificate:
        efdaCertificateFiles.push(file);
        break;
      case ICredentialV2FileType.cprBls:
        cprBlsFiles.push(file);
        break;

      default:
        break;
    }
  });

  return {
    status: entry.status,
    license: '',
    state: entry.state,
    profession: entry.profession,
    school: '',
    subProfession: entry.subProfession,
    addedAt: entry.createdAt,
    credentials: [
      ...(entry.stateLicense
        ? [
            {
              entry_field: {
                license: entry.stateLicense.number,
                issue_date: entry?.stateLicense?.issuedAt
                  ? new Date(entry.stateLicense.issuedAt)
                  : undefined,
                expiration_date: entry?.stateLicense?.expiredAt
                  ? new Date(entry?.stateLicense?.expiredAt)
                  : undefined,
                normalizedLicense: entry.stateLicense.normalizedNumber,
              },
              credential_type: CredentialTypeEnum.StateLicense,
              credential_input_type: CredentialInputTypeEnum.EntryField,
              addedAt: entry?.stateLicense?.createdAt,
              id: entry.stateLicense.id,
              files: [],
            } as ICredential,
          ]
        : []),
      ...(entry.references
        ? [
            {
                entry_field: {
                    references: entry.references.map((item) => ({
                        _id: item.id,
                        contactEmail: item.contactEmail,
                        officeName: item.contactName,
                        employmentStart: new Date(item.employmentStart),
                        employmentEnd: new Date(item.employmentEnd),
                    })),
                    normalizedLicense: '',
                },
                credential_type: CredentialTypeEnum.Reference,
                credential_input_type: CredentialInputTypeEnum.EntryField,
                addedAt: entry?.stateLicense?.createdAt,
                files: [],
            } as unknown as ICredential,
          ]
        : []),
      ...(radiologyFiles.length
        ? [
            {
              credential_type: CredentialTypeEnum.Radiology,
              credential_input_type: CredentialInputTypeEnum.Upload,
              files: radiologyFiles,
            } as ICredential,
          ]
        : []),
      ...(daCertificateFiles.length
        ? [
            {
              credential_type: CredentialTypeEnum.DACertificate,
              credential_input_type: CredentialInputTypeEnum.Upload,
              files: daCertificateFiles,
            } as ICredential,
          ]
        : []),
      ...(efdaCertificateFiles.length
        ? [
            {
              credential_type: CredentialTypeEnum.DACertificate,
              credential_input_type: CredentialInputTypeEnum.Upload,
              files: efdaCertificateFiles,
            } as ICredential,
          ]
        : []),
      ...(cprBlsFiles.length
        ? [
            {
              credential_type: CredentialTypeEnum.CprBls,
              credential_input_type: CredentialInputTypeEnum.Upload,
              files: cprBlsFiles,
            } as ICredential,
          ]
        : []),
    ],

    id: entry.id,
    professionName: UserProfession.getName(entry.profession)!,
  };
}
